const APP_COLUMN_RATIO = 0.75;
const MIN_APP_COLUMN_WIDTH = 390;
const TABLET_MIN_COLUMN_WIDTH = 600;
const WIDE_MIN_COLUMN_WIDTH = 481;

type Viewport = { width: number; height: number };
type AppColumn = {
  width: number;
  left: number;
  isWide: boolean;
  isTablet: boolean;
};

export const computeAppColumn = ({ width, height }: Viewport): AppColumn => {
  const column =
    width <= height
      ? width
      : Math.min(
          width,
          Math.max(height * APP_COLUMN_RATIO, MIN_APP_COLUMN_WIDTH),
        );
  return {
    width: column,
    left: (width - column) / 2,
    isWide: column >= WIDE_MIN_COLUMN_WIDTH,
    isTablet: column >= TABLET_MIN_COLUMN_WIDTH,
  };
};

let activeOwner: symbol | null = null;

export const startAppColumnSync = (win: Window = window): (() => void) => {
  if (activeOwner) return () => {};
  const owner = Symbol('appColumnSync');
  activeOwner = owner;

  const root = win.document.documentElement;
  let stableWidth = win.innerWidth;
  let stableHeight = win.innerHeight;

  const apply = () => {
    if (win.innerWidth === 0 || win.innerHeight === 0) return;
    if (win.innerWidth !== stableWidth) {
      stableWidth = win.innerWidth;
      stableHeight = win.innerHeight;
    } else {
      stableHeight = Math.max(stableHeight, win.innerHeight);
    }
    const column = computeAppColumn({
      width: stableWidth,
      height: stableHeight,
    });
    if (column.width < stableWidth) {
      root.style.setProperty('--app-column-width', `${column.width}px`);
      root.style.setProperty('--app-column-left', `${column.left}px`);
      root.setAttribute('data-app-column', '');
    } else {
      root.style.removeProperty('--app-column-width');
      root.style.removeProperty('--app-column-left');
      root.removeAttribute('data-app-column');
    }
    root.toggleAttribute('data-wide', column.isWide);
    root.toggleAttribute('data-tablet', column.isTablet);
  };

  apply();
  win.addEventListener('resize', apply);

  return () => {
    if (activeOwner !== owner) return;
    win.removeEventListener('resize', apply);
    activeOwner = null;
  };
};
